import { useState, useEffect } from 'react';

import {
  Drawer,
  Toolbar,
  Typography,
  Divider,
  Box,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/material/Grid';
import ManifestCheckNameField from './ManifestCheckNameField';
import ManifestCheckSectionField from './ManifestCheckSectionField';
import ManifestCheckPromptField from './ManifestCheckPromptField';

import {
  updateManifestCheck,
  createManifestCheck,
} from '@/actions/manifestCheck';
import { useApi } from '@/providers/ApiContext';
import normalizeSmartPunctuation from '@/util/normalizeSmartPunctuation';
import ManifestCheckReferenceField from './ManifestCheckReferenceField';
import type { ManifestCheck, EntityId } from '@/types';

const drawerWidth = 400;

interface AdminManifestCheckDrawerProps {
  open: boolean;
  toggleDrawer: () => void;
  manifest_id?: EntityId;
  manifest_check?: ManifestCheck | null;
  setManifestCheck: (check: ManifestCheck) => void;
  global?: boolean;
}

function AdminManifestCheckDrawer({
  open,
  toggleDrawer,
  manifest_id,
  manifest_check,
  setManifestCheck,
  global,
}: AdminManifestCheckDrawerProps): React.JSX.Element {
  const api = useApi();
  const [pristine, setPristine] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [name_error, setNameError] = useState<string | boolean>(false);
  const [section, setSection] = useState<string>('');
  const [section_error, setSectionError] = useState<string | boolean>(false);
  const [critical, setCritical] = useState<boolean>(false);
  const [prompt, setPrompt] = useState<string>('');
  const [prompt_error, setPromptError] = useState<string | boolean>(false);
  const [reference, setReference] = useState<string>('');
  const [reference_error, setReferenceError] = useState<string | boolean>(
    false,
  );

  useEffect(() => {
    if (manifest_check) {
      setName(manifest_check.name || '');
      setSection(global ? 'global' : manifest_check.section || '');
      setCritical(manifest_check.critical || false);
      setPrompt(manifest_check.prompt || '');
      setReference(manifest_check.reference || '');
    } else {
      setName('');
      setSection(global ? 'global' : '');
      setCritical(false);
      setPrompt('');
      setReference('');
    }
    setPristine(true);
    setNameError(false);
    setSectionError(false);
    setPromptError(false);
    setReferenceError(false);
  }, [manifest_check, global]);

  const handleSubmit = async (): Promise<void> => {
    if (!manifest_id || !api || !name) return;
    setLoading(true);

    try {
      const normalized_prompt = normalizeSmartPunctuation(prompt);
      const normalized_reference = normalizeSmartPunctuation(reference);

      if (manifest_check?.entity_id) {
        const payload: {
          name?: string;
          section?: string;
          prompt?: string;
          critical?: boolean;
          reference?: string;
        } = {};
        if (name) payload.name = name;
        if (section) payload.section = section;
        if (normalized_prompt) payload.prompt = normalized_prompt;
        if (critical !== undefined) payload.critical = critical;
        if (normalized_reference) payload.reference = normalized_reference;
        const resp = await updateManifestCheck(
          api,
          manifest_id,
          manifest_check.entity_id,
          payload,
        );
        setManifestCheck(resp);
      } else {
        const payload: {
          name: string;
          section?: string;
          prompt?: string;
          critical?: boolean;
          reference?: string;
        } = {
          name,
        };
        if (section) payload.section = section;
        if (normalized_prompt) payload.prompt = normalized_prompt;
        if (critical !== undefined) payload.critical = critical;
        if (normalized_reference) payload.reference = normalized_reference;
        const resp = await createManifestCheck(api, manifest_id, payload);
        setManifestCheck(resp);
      }
      toggleDrawer();
    } catch (error) {
      console.error('Error saving manifest check:', error);
      setLoading(false);
    }
  };

  return (
    <>
      <Drawer
        anchor='right'
        open={open}
        onClose={toggleDrawer}
        sx={{
          height: '100%',
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
          zIndex: (theme) => theme.zIndex.appBar - 1, // Ensure Drawer is behind AppBar
        }}
      >
        <Toolbar />
        <Toolbar
          sx={{
            minHeight: '55px !important',
          }}
        >
          <Typography variant='h6' sx={{ mr: 4 }}>
            {manifest_check ? `Edit Check` : 'New Check'}
          </Typography>
        </Toolbar>
        <Divider />
        <Box padding='15px'>
          <Grid container spacing={2}>
            <Grid size={12}>
              <ManifestCheckNameField
                value={name}
                setValue={setName}
                error={name_error}
                setError={setNameError}
                setChanged={() => setPristine(false)}
              />
            </Grid>
            {!global ? (
              <Grid size={12}>
                <ManifestCheckSectionField
                  value={section}
                  setValue={setSection}
                  error={section_error}
                  setError={setSectionError}
                  setChanged={() => setPristine(false)}
                  isEditing={Boolean(manifest_check)}
                />
              </Grid>
            ) : undefined}
            {!global ? (
              <Grid size={12}>
                <ManifestCheckReferenceField
                  value={reference}
                  setValue={setReference}
                  error={reference_error}
                  setError={setReferenceError}
                  setChanged={() => setPristine(false)}
                  isEditing={Boolean(manifest_check)}
                />
              </Grid>
            ) : undefined}
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={critical}
                    onChange={() => {
                      setPristine(false);
                      setCritical(!critical);
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
                label='Critical Requirement'
              />
            </Grid>
            <Grid size={12}>
              <ManifestCheckPromptField
                value={prompt}
                setValue={setPrompt}
                error={prompt_error}
                setError={setPromptError}
                setChanged={() => setPristine(false)}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Divider />
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <LoadingButton
            variant='contained'
            onClick={handleSubmit}
            loading={loading}
            disabled={
              pristine ||
              Boolean(name_error) ||
              Boolean(section_error) ||
              Boolean(prompt_error) ||
              !Boolean(name) ||
              !Boolean(prompt) ||
              Boolean(reference_error)
            }
          >
            Submit
          </LoadingButton>
          <Button variant='outlined' onClick={toggleDrawer} disabled={loading}>
            Cancel
          </Button>
        </Toolbar>
      </Drawer>
    </>
  );
}

export default AdminManifestCheckDrawer;
